import { useState, useEffect } from "react";
import { Star, Quote } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default testimonials (fallback)
const defaultTestimonials = [
  {
    id: "1",
    quote: "Mein Sohn hat in nur wenigen Wochen enorme Fortschritte gemacht. Seine Ballkontrolle ist viel besser und er hat endlich mehr Selbstvertrauen auf dem Platz.",
    author: "Vater von Emir",
    player_age: 12,
    rating: 5,
    type: "parent",
  },
  {
    id: "2",
    quote: "Wir haben vorher verschiedene Trainings ausprobiert, aber hier sieht man wirklich Ergebnisse. Die Einheiten sind strukturiert und motivierend – mein Kind freut sich auf jedes Training.",
    author: "Mutter von Leon",
    player_age: 10,
    rating: 5,
    type: "parent",
  },
  {
    id: "3",
    quote: "Nach dem Training wurde mein Sohn im Spiel viel selbstbewusster. Er traut sich mehr zu und das sieht man sofort.",
    author: "Vater von Yusuf",
    player_age: 13,
    rating: 5,
    type: "parent",
  },
  {
    id: "4",
    quote: "Ich habe gelernt, den Ball viel besser zu kontrollieren und meine Technik hat sich stark verbessert. Das Training macht richtig Spaß!",
    author: "Ali",
    player_age: 11,
    rating: 5,
    type: "player",
  },
  {
    id: "5",
    quote: "Vorher war ich unsicher mit dem Ball, jetzt fühle ich mich viel sicherer und spiele besser in meinem Team.",
    author: "Can",
    player_age: 12,
    rating: 5,
    type: "player",
  },
  {
    id: "6",
    quote: "Man sieht schon nach wenigen Einheiten deutliche Fortschritte. Genau das, was wir gesucht haben.",
    author: "Eltern eines Jugendspielers",
    player_age: null,
    rating: 5,
    type: "parent",
  },
];

// Get initials from name
const getInitials = (name) => {
  const words = name.split(" ");
  if (words.length >= 2) {
    return `${words[0][0]}${words[words.length - 1][0]}`.toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

// Get avatar color based on name
const getAvatarColor = (name) => {
  const colors = [
    "from-[#c1ff72] to-[#8bc34a]",
    "from-[#64b5f6] to-[#1976d2]",
    "from-[#ba68c8] to-[#7b1fa2]",
    "from-[#ffb74d] to-[#f57c00]",
    "from-[#4db6ac] to-[#00796b]",
    "from-[#e57373] to-[#d32f2f]",
  ];
  const index = name.charCodeAt(0) % colors.length;
  return colors[index];
};

// Star Rating Component
const StarRating = ({ rating }) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5].map((star) => (
      <Star
        key={star}
        className={`w-4 h-4 ${
          star <= rating
            ? "text-[#c1ff72] fill-[#c1ff72]"
            : "text-[#3A3A3A]"
        }`}
      />
    ))}
  </div>
);

// Testimonial Card Component
const TestimonialCard = ({ testimonial, featured = false }) => {
  const initials = getInitials(testimonial.author);
  const avatarColor = getAvatarColor(testimonial.author);

  return (
    <div
      className={`bg-[#1A1A1A] border border-white/10 p-6 h-full flex flex-col transition-all duration-300 hover:border-[#c1ff72]/30 ${
        featured ? "md:col-span-2 lg:col-span-1" : ""
      }`}
      data-testid={`testimonial-${testimonial.id}`}
    >
      {/* Quote Icon */}
      <div className="mb-4">
        <Quote className="w-8 h-8 text-[#c1ff72]/30" />
      </div>

      {/* Rating */}
      <div className="mb-4">
        <StarRating rating={testimonial.rating} />
      </div>

      {/* Quote Text */}
      <p className="text-white/90 text-base leading-relaxed flex-grow mb-6">
        &ldquo;{testimonial.quote}&rdquo;
      </p>

      {/* Author */}
      <div className="flex items-center gap-4 mt-auto pt-4 border-t border-white/10">
        {/* Avatar */}
        <div
          className={`w-12 h-12 rounded-full bg-gradient-to-br ${avatarColor} flex items-center justify-center flex-shrink-0`}
        >
          <span className="text-white font-bold text-sm">{initials}</span>
        </div>

        {/* Name & Age */}
        <div>
          <p className="text-white font-semibold">{testimonial.author}</p>
          {testimonial.player_age && (
            <p className="text-[#A1A1AA] text-sm">
              {testimonial.type === "player" ? `${testimonial.player_age} Jahre` : `(${testimonial.player_age})`}
            </p>
          )}
        </div>

        {/* Badge */}
        {testimonial.type === "player" && (
          <div className="ml-auto">
            <span className="bg-[#c1ff72]/10 text-[#c1ff72] text-xs px-2 py-1 font-medium">
              SPIELER
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export const TestimonialsSection = () => {
  const [testimonials, setTestimonials] = useState(defaultTestimonials);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await axios.get(`${API}/testimonials`);
        if (response.data && response.data.length > 0) {
          setTestimonials(response.data);
        }
      } catch (error) {
        // Using default testimonials as fallback
      } finally {
        setIsLoading(false);
      }
    };
    fetchTestimonials();
  }, []);

  return (
    <section
      id="bewertungen"
      className="py-20 lg:py-32 bg-[#0A0A0A]"
      data-testid="testimonials-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p className="text-xs uppercase tracking-[0.2em] text-[#c1ff72] font-semibold mb-4">
            Erfahrungen
          </p>
          <h2 className="font-['Bebas_Neue'] text-4xl md:text-6xl lg:text-7xl uppercase tracking-tighter text-white mb-6">
            WAS SPIELER UND <span className="text-[#c1ff72]">ELTERN</span> SAGEN
          </h2>
          <p className="text-[#A1A1AA] text-lg max-w-2xl mx-auto">
            Echte Erfahrungen von Familien, die bereits vom Training profitieren
          </p>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-3 gap-4 mb-12 max-w-2xl mx-auto">
          <div className="text-center p-4 bg-[#1A1A1A] border border-white/10">
            <p className="font-['Bebas_Neue'] text-3xl md:text-4xl text-[#c1ff72]">100+</p>
            <p className="text-[#A1A1AA] text-sm">Trainierte Spieler</p>
          </div>
          <div className="text-center p-4 bg-[#1A1A1A] border border-white/10">
            <p className="font-['Bebas_Neue'] text-3xl md:text-4xl text-[#c1ff72]">5.0</p>
            <p className="text-[#A1A1AA] text-sm">Durchschnittsbewertung</p>
          </div>
          <div className="text-center p-4 bg-[#1A1A1A] border border-white/10">
            <p className="font-['Bebas_Neue'] text-3xl md:text-4xl text-[#c1ff72]">98%</p>
            <p className="text-[#A1A1AA] text-sm">Empfehlungsrate</p>
          </div>
        </div>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.slice(0, 6).map((testimonial) => (
            <TestimonialCard key={testimonial.id} testimonial={testimonial} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
